import {
  TableRowPlaceholder,
  TdPlaceholder,
} from 'components/superreports/widgets/table/TableRowPlaceholder';
import { ContentFrame } from 'components/ui-blocks/container';
import { Table, Tbody, Th, Thead, Tr } from 'components/ui-blocks/table';
import { useIsListEmpty } from 'hooks/useIsListEmpty';
import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getUserPermissions } from 'redux/selectors/user';
import ForecastEmptyPlaceholder from 'routes/forecast/components/ForecastEmptyPlaceholder';
import ForecastTableBatchActions from 'routes/forecast/components/ForecastTableBatchActions';
import useForecastBatchActions from 'routes/forecast/hooks/useForecastBatchActions';
import type { ThunkDispatch } from 'types/redux';
import {
  editForecastId,
  fetchPaginatedForecastsSmartly,
  viewForecastId,
} from '../redux/modules/forecast.actions';
import {
  getFilteredForecasts,
  getForecastByProject,
  getForecastLoadingState,
  getForecastSelectionsAsSet,
} from '../redux/modules/forecast.selectors';
import styles from './ForecastTable.module.scss';
import ForecastProjectTr from './table/ForecastProjectTr';

type Props = {};

const ForecastsByProjectTable: React.FC<Props> = () => {
  const dispatch = useDispatch<ThunkDispatch>();
  const forecasts = useSelector(getFilteredForecasts);
  const batchActionProps = useForecastBatchActions(forecasts.map(f => f.id));
  const selections = useSelector(getForecastSelectionsAsSet);
  const forecastNestedList = useSelector(getForecastByProject);
  const isLoading = useSelector(getForecastLoadingState);
  const { isEmpty } = useIsListEmpty(forecastNestedList.length, isLoading);
  const permissions = useSelector(getUserPermissions);

  const handleOpenRow = useCallback(
    (id: number) => {
      dispatch(fetchPaginatedForecastsSmartly({ project_ids: [id] }));
    },
    [dispatch]
  );

  const clickForecast = useCallback(
    (id: number, mode?: 'edit' | 'view') => {
      if (mode === 'edit') {
        dispatch(editForecastId(id, { interaction: 'Click Task' }));
      } else {
        dispatch(viewForecastId(id, { interaction: 'Click Task' }));
      }
    },
    [dispatch]
  );

  if (isEmpty) {
    return <ForecastEmptyPlaceholder isLoading={isLoading} />;
  }

  function renderBody() {
    if (isLoading && forecastNestedList.length === 0) {
      return <TableRowPlaceholder rowCount={20} colCount={8} placeHolder={<TdPlaceholder />} />;
    }
    return forecastNestedList.map(row => {
      return (
        <ForecastProjectTr
          onClickForecast={clickForecast}
          key={row.project.id}
          row={row}
          selections={selections}
          canUpdate={permissions.canUpdate.forecasts}
          canDelete={permissions.canDelete.forecasts}
          onOpenRow={handleOpenRow}
        />
      );
    });
  }

  return (
    <>
      <ForecastTableBatchActions batchActions={batchActionProps} isLoading={isLoading} />
      <ContentFrame spacing>
        <Table className={styles.container} inline rowHover>
          <Thead>
            <Tr>
              <Th colSpan={5}>Projects</Th>
              <Th>Estimate</Th>
              <Th>Tasks</Th>
            </Tr>
          </Thead>
          <Tbody>{renderBody()}</Tbody>
        </Table>
      </ContentFrame>
    </>
  );
};

export default ForecastsByProjectTable;
